import ConfirmDialog from '@/components/ui/ConfirmDialog';
import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Search, FileText, ExternalLink, Download, BookOpen, Scale, Lightbulb, Plus, Trash2 } from 'lucide-react';
import { useOrganisation } from '@/lib/OrganisationContext';
import { useAuth } from '@/lib/AuthContext';
import ResourceUploadDialog from '@/components/resources/ResourceUploadDialog';
import { fileAction, filterResources } from '@/lib/resourcePresentation';
import { operationMatches } from '@/lib/operationGuard';

const CATEGORY_CONFIG = {
  legislation: { label: 'Legislation', icon: Scale, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  guidance: { label: 'Guidance', icon: BookOpen, color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200' },
  template: { label: 'Templates', icon: FileText, color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-200' },
  case_study: { label: 'Case Studies', icon: Lightbulb, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
};

export default function Resources() {
  const { selectedOrg } = useOrganisation();
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterGeography, setFilterGeography] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [removePrompt, setRemovePrompt] = useState(null);
  const removalReturnRef = useRef(null);
  const [status, setStatus] = useState(null);
  const addButtonRef = useRef(null);
  const organisationLibraryHeadingRef = useRef(null);
  const loadGeneration = useRef(0);
  const removalGeneration = useRef(0);
  const selectedOrgRef = useRef(selectedOrg?.id);
  const membership = user?.organisations?.find((item) => item.id === selectedOrg?.id);
  const canManage = Boolean(user?.is_provider || ['owner', 'admin'].includes(membership?.role || user?.org_role));

  useEffect(() => {
    selectedOrgRef.current = selectedOrg?.id; removalGeneration.current += 1;
    setResources([]); setLoading(true); setShowAddForm(false);
    setRemovingId(null); setStatus(null); setRemovePrompt(null);
    loadResources();
  }, [selectedOrg?.id]);

  useEffect(() => () => { loadGeneration.current += 1; removalGeneration.current += 1; }, []);

  async function loadResources() {
    const token = ++loadGeneration.current; const target = selectedOrg?.id;
    try {
      const list = await base44.entities.ResourceItem.filter({ is_active: true }, 'category', 200);
      if (loadGeneration.current === token && selectedOrg?.id === target) setResources(list);
    } catch (e) {
      console.error(e);
    } finally {
      if (loadGeneration.current === token && selectedOrg?.id === target) setLoading(false);
    }
  }

  const sectors = [...new Set(resources.map(r => r.sector).filter(Boolean))];
  const geographies = [...new Set(resources.map(r => r.geography).filter(Boolean))];

  const countPool = filterResources(resources, { search, sector: filterSector, geography: filterGeography });
  const filtered = filterResources(countPool, { category: filterCategory });

  async function removeResource(resource, trigger) {
    const focusAfterConfirmation = document.activeElement;
    const token = ++removalGeneration.current; const orgId = selectedOrgRef.current;
    setRemovingId(resource.id); setStatus(null);
    try {
      await base44.entities.ResourceItem.delete(resource.id);
      if (!operationMatches(token, removalGeneration.current, orgId, selectedOrgRef.current)) return;
      const activeElement = document.activeElement;
      const restoreFocus = activeElement === focusAfterConfirmation
        || activeElement === trigger
        || activeElement === document.body
        || !activeElement?.isConnected;
      setResources((current) => current.filter((item) => item.id !== resource.id));
      setStatus({ kind: 'success', text: `${resource.title} was removed.` });
      if (restoreFocus) removalReturnRef.current = organisationLibraryHeadingRef.current;
    } catch (error) { if (operationMatches(token, removalGeneration.current, orgId, selectedOrgRef.current)) throw error; }
    finally { if (operationMatches(token, removalGeneration.current, orgId, selectedOrgRef.current)) setRemovingId(null); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" aria-label="Loading resources" />
      </div>
    );
  }

  return (
    <div className="resources-page max-w-5xl mx-auto space-y-6">
      {removePrompt && <ConfirmDialog title={`Remove ${removePrompt.title}?`} description="This removes the document from your organisation’s library. Other organisation resources are unchanged." returnFocusRef={removalReturnRef} onClose={() => setRemovePrompt(null)} onConfirm={() => removeResource(removePrompt, removalReturnRef.current)} />}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="resource-on-dark text-xl sm:text-2xl font-bold text-foreground">Resource Library</h1>
          <p className="resource-on-dark text-sm text-muted-foreground mt-1">Legislation, guidance, and templates for your sector and region</p>
        </div>
        {canManage ? <button
          ref={addButtonRef}
          aria-label="Upload resource"
          onClick={() => setShowAddForm(true)}
          className="resource-primary flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-800 text-white text-sm font-semibold hover:bg-teal-900 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          <span className="hidden sm:inline">Upload resource</span>
        </button> : <p className="text-sm text-muted-foreground">You have read-only access.</p>}
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Resource categories">
        <button
          aria-pressed={!filterCategory}
          onClick={() => setFilterCategory('')}
          className={`min-h-11 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${!filterCategory ? 'resource-primary bg-teal-800 text-white' : 'bg-card border border-border text-muted-foreground hover:bg-muted'}`}
        >
          All ({countPool.length})
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
          const count = countPool.filter(r => r.category === key).length;
          return (
            <button
              key={key}
              aria-pressed={filterCategory === key}
              onClick={() => setFilterCategory(filterCategory === key ? '' : key)}
              className={`flex min-h-11 items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${filterCategory === key ? 'resource-primary bg-teal-800 text-white' : 'bg-card border border-border text-muted-foreground hover:bg-muted'}`}
            >
              <cfg.icon className="w-3.5 h-3.5" aria-hidden="true" />
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {status && <p role={status.kind === 'error' ? 'alert' : 'status'} className={`rounded-lg border p-3 text-sm ${status.kind === 'error' ? 'border-red-300 bg-red-50 text-red-800' : 'border-teal-300 bg-teal-50 text-teal-900'}`}>{status.text}</p>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search resources…"
            aria-label="Search resources"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-card text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          />
        </div>
        {sectors.length > 0 && (
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} aria-label="Filter by sector" className="border border-border rounded-lg px-3 py-2 bg-card text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
            <option value="">All Sectors</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {geographies.length > 0 && (
          <select value={filterGeography} onChange={e => setFilterGeography(e.target.value)} aria-label="Filter by region" className="border border-border rounded-lg px-3 py-2 bg-card text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
            <option value="">All Regions</option>
            {geographies.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
      </div>

      {/* Resource list */}
      <div className="space-y-8">
          {[{ title: 'DI library', rows: filtered.filter((item) => !item.organisation_id) }, { title: "Your organisation's library", rows: filtered.filter((item) => item.organisation_id === selectedOrg?.id) }].map((section) => <section key={section.title} aria-labelledby={`resource-${section.title.replace(/\W/g, '-').toLowerCase()}`}>
          <h2 ref={section.title === "Your organisation's library" ? organisationLibraryHeadingRef : undefined} tabIndex={section.title === "Your organisation's library" ? -1 : undefined} id={`resource-${section.title.replace(/\W/g, '-').toLowerCase()}`} className="resource-on-dark mb-3 text-lg font-semibold text-foreground">{section.title}</h2>
          {section.rows.length === 0 ? <p className="resource-on-dark rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground">No resources in this library.</p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{section.rows.map(res => {
            const cfg = CATEGORY_CONFIG[res.category] || CATEGORY_CONFIG.guidance;
            const Icon = cfg.icon;
            const action = fileAction(res);
            return (
              <div key={res.id} className={`resource-card bg-card border ${cfg.border} rounded-xl p-5 shadow-sm flex flex-col`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-5 h-5 ${cfg.color}`} aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-foreground">{res.title}</h3>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                      {res.geography && <span className="text-[10px] text-muted-foreground">{res.geography}</span>}
                      {res.sector && <span className="text-[10px] text-muted-foreground">· {res.sector}</span>}
                    </div>
                  </div>
                </div>
                {res.description && <p className="text-xs text-muted-foreground mb-3 flex-1">{res.description}</p>}
                <div className="flex gap-2">
                  {res.file_url && (
                    <a
                      href={res.file_url}
                      target={action.download ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      download={action.download ? '' : undefined}
                      className="resource-primary flex min-h-11 items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-teal-800 text-white hover:bg-teal-900 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" aria-hidden="true" />
                      {action.label}{action.download ? '' : ' (opens in new tab)'}
                    </a>
                  )}
                  {res.external_url && (
                    <a
                      href={res.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      Open related website for {res.title} (opens in new tab)
                    </a>
                  )}
                  {canManage && res.organisation_id === selectedOrg?.id && <button type="button" onClick={(event) => { removalReturnRef.current = event.currentTarget; setRemovePrompt(res); }} disabled={Boolean(removingId)} className="flex min-h-11 items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50" aria-label={`Remove ${res.title}`}><Trash2 className="h-3.5 w-3.5" aria-hidden="true" />{removingId === res.id ? 'Removing…' : 'Remove'}</button>}
                </div>
              </div>
            );
          })}</div>}
          </section>)}
      </div>

      <ResourceUploadDialog open={showAddForm} onOpenChange={setShowAddForm} returnFocusRef={addButtonRef} organisationId={selectedOrg?.id} onSaved={() => loadResources()} />
    </div>
  );
}
